import { useSeo } from "../hooks/useSeo";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ArrowDown } from "lucide-react";
import { PRODUCTS as LOCAL_PRODUCTS, ORIGINS, TYPES, variantLabel, priceFromLabel } from "../data/products";
import { useRevealAll } from "../hooks/useReveal";
import useStripeProducts from "../hooks/useStripeProducts";

const ORIGIN_FILTERS = [{ value: "all", label: "Összes" }, ...ORIGINS];

// Price helper for dynamic products (Stripe unit_amount already in HUF, zero-decimal).
const formatHufAmount = (n) =>
  typeof n === "number" ? new Intl.NumberFormat("hu-HU").format(n) + " Ft" : "- Ft";

const priceFromDynamic = (product) => {
  const prices = (product.variants || [])
    .map((v) => v.unit_amount)
    .filter((n) => typeof n === "number");
  if (!prices.length) return "- Ft";
  return formatHufAmount(Math.min(...prices));
};

export default function Products() {
  useSeo({
    title: "Póthaj webshop – európai és kazahsztáni hajcopfok | Hair Elixir Hungary",
    description: "Vásárolj prémium emberi póthajat: festetlen európai és kazahsztáni hajcopfok több hosszban és tömegben, egyenes, hullámos és göndör textúrában.",
  });
  const [params, setParams] = useSearchParams();
  const [origin, setOrigin] = useState(params.get("o") || "all");
  const ref = useRef(null);
  const { products: stripeProducts, loading } = useStripeProducts();

  // Fully dynamic: only Stripe drives the catalog. Empty array while loading.
  const PRODUCTS = stripeProducts || [];

  useEffect(() => {
    const next = new URLSearchParams();
    if (origin !== "all") next.set("o", origin);
    setParams(next, { replace: true });
  }, [origin, setParams]);

  const counts = useMemo(() => {
    const c = { all: PRODUCTS.length };
    PRODUCTS.forEach((p) => {
      c[p.origin] = (c[p.origin] || 0) + 1;
    });
    return c;
  }, [PRODUCTS]);

  const filtered = useMemo(
    () => PRODUCTS.filter((p) => origin === "all" || p.origin === origin),
    [origin, PRODUCTS],
  );

  // Re-run reveal observer when filter changes
  useRevealAll(ref, [filtered.length, origin]);

  const scrollToGrid = () => {
    const el = document.getElementById("collection-grid");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main ref={ref} data-testid="page-products" className="bg-[color:var(--he-bg)]">
      {/* === COVER HERO === */}
      <section className="relative h-[78vh] min-h-[620px] bg-[color:var(--he-ink)] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt=""
            className="w-full h-full object-cover he-ken-burns"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/15" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />
        </div>

        <div className="relative z-10 h-full grid grid-rows-[auto_1fr]">
          {/* TOP — masthead */}
          <div className="pt-28 md:pt-32">
            <div className="he-container">
              <div className="flex items-center justify-between text-white/85">
                <span className="overline-xs" style={{ letterSpacing: "0.34em" }}>
                  Kollekció
                </span>
                <span className="hidden sm:block h-px flex-1 mx-8 bg-[color:var(--he-gold)]/40" />
                <span className="overline-xs" style={{ letterSpacing: "0.34em" }}>
                  {String(PRODUCTS.length).padStart(2, "0")} elérhető termék
                </span>
              </div>
            </div>
          </div>

          {/* MIDDLE — content, end-aligned */}
          <div className="flex items-end pb-16 md:pb-24">
            <div className="he-container w-full">
              <div className="max-w-4xl">
                <div
                  className="flex items-center gap-5 mb-10 he-rise"
                  style={{ animationDelay: "200ms" }}
                >
                  <span className="block w-12 h-px bg-[color:var(--he-gold)]" />
                  <span
                    className="text-[color:var(--he-gold)] uppercase text-xs"
                    style={{ letterSpacing: "0.34em", fontWeight: 500 }}
                  >
                    Aktuálisan elérhető tételek
                  </span>
                </div>
                <h1
                  className="font-serif-display text-white tracking-[-0.022em] he-rise"
                  style={{
                    animationDelay: "350ms",
                    fontWeight: 400,
                    fontSize: "clamp(2.6rem, 7vw, 7rem)",
                    lineHeight: 1.04,
                    textShadow: "0 4px 60px rgba(0,0,0,0.6)",
                  }}
                >
                  A teljes <span className="italic" style={{ fontWeight: 300 }}>kollekció.</span>
                </h1>
                <p
                  className="mt-8 max-w-xl text-white/85 font-light text-base md:text-lg leading-[1.7] he-rise"
                  style={{ animationDelay: "550ms", textShadow: "0 2px 20px rgba(0,0,0,0.55)" }}
                >
                  {PRODUCTS.length} kiválasztott copf — európai és
                  kazahsztáni forrásból. Minden tétel egyedi: konkrét hossz,
                  súly és szín, kéznél van.
                </p>
                <button
                  onClick={scrollToGrid}
                  className="mt-10 btn-arrow text-white he-rise"
                  style={{ animationDelay: "750ms" }}
                  data-testid="cover-cta"
                >
                  <span className="arrow-circle">
                    <ArrowDown size={18} className="arrow-icon text-white" />
                  </span>
                  <span style={{ fontWeight: 500 }}>Termékek megtekintése</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === FILTER BAR + GRID === */}
      <section
        id="collection-grid"
        className="py-20 md:py-28 bg-[color:var(--he-bg-alt)] scroll-mt-24"
      >
        <div className="he-container">
          {/* Section heading + sticky filter row */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7 reveal">
              <p className="overline mb-5 mag-marker">
                <span>Termékek · {String(filtered.length).padStart(2, "0")} darab</span>
              </p>
              <h2
                className="font-serif-display tracking-[-0.018em]"
                style={{
                  fontWeight: 400,
                  fontSize: "clamp(2.2rem, 4.6vw, 4.4rem)",
                  lineHeight: 1.05,
                }}
              >
                Szűrj{" "}
                <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                  származás
                </span>
                {" "}szerint,
                <br className="hidden sm:block" />
                vagy nézd meg az összeset.
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9 reveal delay-1">
              <p className="text-[color:var(--he-ink-soft)] font-light leading-[1.7] text-[15px]">
                Két csoport: európai és kazahsztáni. Ha nem tudod, melyik
                illik hozzád, töltsd ki a 60 másodperces választót.
              </p>
              <Link
                to="/kerdoiv"
                className="btn-ghost mt-5 text-[color:var(--he-ink)]"
                data-testid="grid-link-quiz"
              >
                <span>Melyik nekem való?</span>
                <ArrowUpRight size={14} className="ghost-arrow" />
              </Link>
            </div>
          </div>

          {/* Single horizontal filter row — origin only */}
          <div className="border-y border-[color:var(--he-line)] py-5 mb-14 reveal">
            <div className="flex items-center gap-4 md:gap-6">
              <div className="flex items-center gap-2 md:gap-3 overflow-x-auto flex-1 min-w-0 -mx-1 px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <span className="overline-xs text-[color:var(--he-ink-soft)] shrink-0 mr-2">
                  Eredet
                </span>
                {ORIGIN_FILTERS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setOrigin(o.value)}
                    data-testid={`filter-origin-${o.value}`}
                    className={`he-pill shrink-0 ${origin === o.value ? "active" : ""}`}
                  >
                    {o.label}
                    <span className="ml-2 opacity-60" style={{ fontSize: "0.55rem" }}>
                      {String(counts[o.value] || 0).padStart(2, "0")}
                    </span>
                  </button>
                ))}
              </div>

              <div
                className="shrink-0 flex items-baseline gap-2 pl-3 md:pl-5 border-l border-[color:var(--he-line)]"
                data-testid="filter-count"
              >
                <span className="figure italic text-[color:var(--he-gold)] text-2xl">
                  {String(filtered.length).padStart(2, "0")}
                </span>
                <span className="overline-xs text-[color:var(--he-ink-soft)] hidden sm:inline">
                  találat
                </span>
              </div>
            </div>
          </div>

          {/* Single uniform grid */}
          {filtered.length === 0 ? (
            <div className="py-24 text-center text-[color:var(--he-ink-soft)]">
              <p className="overline mb-4">Nincs találat</p>
              <p className="font-serif-display text-2xl mb-6">
                Próbálj másik szűrőt — vagy nézd meg az összeset.
              </p>
              <button
                onClick={() => setOrigin("all")}
                className="btn-ghost text-[color:var(--he-ink)]"
              >
                <span>Szűrők törlése</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 md:gap-x-10 gap-y-14 md:gap-y-20">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* === FOOTER CTA === */}
      <section className="bg-[color:var(--he-cream)] py-24 md:py-32 border-t border-[color:var(--he-line)]">
        <div className="he-container">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <p className="overline mb-5">Még nem tudod, melyiket válaszd?</p>
              <h3
                className="font-serif-display leading-[1.0]"
                style={{ fontWeight: 400, fontSize: "clamp(2rem, 4.4vw, 3.6rem)" }}
              >
                Töltsd ki a{" "}
                <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                  60 másodperces
                </span>{" "}
                választót.
              </h3>
              <p className="mt-6 max-w-xl text-[color:var(--he-ink-soft)] font-light leading-relaxed">
                Öt kérdés a hajadról és a kívánt eredményről. A végén
                megmutatjuk, melyik termékek illenek hozzád.
              </p>
            </div>
            <div className="md:col-span-5 md:pl-8 flex flex-col sm:flex-row gap-4 md:items-center md:justify-end">
              <Link to="/kerdoiv" className="btn-arrow text-[color:var(--he-ink)]" data-testid="footer-quiz-cta">
                <span className="arrow-circle">
                  <ArrowRight size={18} className="arrow-icon" />
                </span>
                <span style={{ fontWeight: 500 }}>Melyik nekem való?</span>
              </Link>
              <Link to="/kapcsolat" className="btn-ghost text-[color:var(--he-ink)]" data-testid="footer-b2b-cta">
                <span>Kérdés? Írj nekünk</span>
                <ArrowUpRight size={14} className="ghost-arrow" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

/* --------- Product Card (uniform) --------- */
function ProductCard({ product, index }) {
  const hasVariants = product.variants && product.variants.length > 0;
  const singleVariant = hasVariants && product.variants.length === 1;
  const variantLabelText = singleVariant
    ? (product.variants[0].length || product.variants[0].weight
        ? variantLabel(product.variants[0])
        : "Egyedi tétel")
    : `${product.variants.length} méret`;
  return (
    <Link
      to={`/termek/${product.stripe_product_id}/${product.slug}`}
      data-testid={`product-card-${product.stripe_product_id}`}
      className="group he-card flex flex-col reveal"
    >
      <div className="he-img-wrap aspect-[3/4]">
        <img
          src={product.image}
          alt={product.name}
          className="he-img w-full h-full object-cover"
          loading="lazy"
        />
        {/* Number top-left */}
        <span className="absolute top-4 left-4 figure italic text-[color:var(--he-gold)] text-2xl md:text-3xl drop-shadow">
          {String(index + 1).padStart(2, "0")}
        </span>
        {/* Origin badge top-right */}
        <span
          className="absolute top-5 right-4 overline-xs text-white px-2.5 py-0.5 border border-white/45 backdrop-blur-sm bg-black/20"
          style={{ fontSize: "0.55rem" }}
        >
          {product.origin === "europai" ? "EU" : "KZ"}
        </span>

        {/* Hover bottom strip */}
        <div className="absolute bottom-0 left-0 right-0 p-4 card-meta-hover bg-gradient-to-t from-black/75 via-black/35 to-transparent text-white">
          <div className="flex items-center justify-between text-xs uppercase" style={{ letterSpacing: "0.22em" }}>
            <span>{variantLabelText}</span>
            <span className="inline-flex items-center gap-2">
              Megnézem <ArrowUpRight size={14} />
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="overline-xs text-[color:var(--he-ink-soft)] mb-1.5" style={{ fontSize: "0.58rem" }}>
            {product.origin === "europai" ? "Európai" : "Kazahsztáni"}
          </p>
          <h3
            className="font-serif-display text-lg md:text-[1.3rem] leading-[1.18]"
            style={{ fontWeight: 400 }}
          >
            {product.name}
          </h3>
          <p className="mt-1.5 text-xs text-[color:var(--he-ink-soft)] font-light leading-relaxed line-clamp-2">
            {variantLabelText}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-serif-display text-base md:text-lg" style={{ fontWeight: 400 }}>
            {priceFromDynamic(product)}
          </p>
        </div>
      </div>

      {/* Variant pills — only when there are named variants */}
      {hasVariants && (product.variants[0].length || product.variants[0].weight) && (
        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          {product.variants.map((v, i) => (
            <span
              key={`${v.length}-${v.weight}-${i}`}
              className="text-[10px] uppercase text-[color:var(--he-ink-soft)] border border-[color:var(--he-line)] px-2 py-0.5 rounded-full group-hover:border-[color:var(--he-gold)]/50 transition-colors"
              style={{ letterSpacing: "0.18em" }}
            >
              {variantLabel(v)}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}
